import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Person from "@mui/icons-material/Person";
import Send from "@mui/icons-material/Send";
import SmartToy from "@mui/icons-material/SmartToy";
import { AppBar, Avatar, Box, IconButton, keyframes, Paper, Stack, TextField, Toolbar, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { INK, SURFACE } from "../../theme/tokens";
import type { Message } from "../../utils/models";

interface MessageBubbleProps {
	isUser: boolean;
}

const blink = keyframes`
  0% { opacity: 0.2; }
  20% { opacity: 1; }
  100% { opacity: 0.2; }
`;

const TypingDot = styled("span")(({ theme }) => ({
	display: "inline-block",
	width: "8px",
	height: "8px",
	borderRadius: "50%",
	backgroundColor: theme.palette.text.primary,
	margin: "0 2px",
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(1.5, 2),
	borderRadius: theme.spacing(2),
	borderBottomLeftRadius: theme.spacing(0.5),
	backgroundColor: theme.palette.grey[100],
	maxWidth: "70px",
	boxShadow: theme.shadows[1],
}));

const MessageContainer = styled(Box)(({ theme }) => ({
	flex: 1,
	padding: theme.spacing(2),
	overflowY: "auto",
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1),
}));

const MessageBubble = styled(Paper, {
	shouldForwardProp: (prop) => prop !== "isUser",
})<MessageBubbleProps>(({ theme, isUser }) => ({
	padding: theme.spacing(1.5, 2),
	maxWidth: "70%",
	alignSelf: isUser ? "flex-end" : "flex-start",
	backgroundColor: isUser ? theme.palette.primary.light : theme.palette.grey[100],
	color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
	borderRadius: theme.spacing(2),
	borderBottomRightRadius: isUser ? theme.spacing(0.5) : theme.spacing(2),
	borderBottomLeftRadius: isUser ? theme.spacing(2) : theme.spacing(0.5),
	boxShadow: theme.shadows[1],
}));

const StreamingMessage = ({ message, speed = 30 }: { message: string; speed?: number }) => {
	const [displayedText, setDisplayedText] = useState("");
	const [isComplete, setIsComplete] = useState(false);
	const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		setDisplayedText("");
		setIsComplete(false);

		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		let charIndex = 0;

		intervalRef.current = setInterval(() => {
			if (charIndex < message.length) {
				setDisplayedText(message.substring(0, charIndex + 1));
				charIndex += 1;
			} else {
				setIsComplete(true);
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
			}
		}, speed);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [message, speed]);

	return (
		<>
			{displayedText}
			{!isComplete && <span className="cursor">|</span>}
		</>
	);
};

interface IProps {
	messages: Message[];
	connected: boolean;
	onSendMessage: (e: FormEvent) => void;
	inputMessage: string;
	setInputMessage: (message: string) => void;
	typing?: boolean;
	userImage?: string;
}

export const Chat = ({ messages, connected, onSendMessage, inputMessage, setInputMessage, typing = false, userImage = "" }: IProps) => {
	const messageEndRef = useRef<HTMLDivElement>(null);
	const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);

	useEffect(() => {
		messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages.length, typing]);

	useEffect(() => {
		if (messages.length > 0) {
			const lastMessage = messages[messages.length - 1];
			if (lastMessage.sender !== "user") {
				setStreamingMessageId(lastMessage.id as string);

				const streamingDuration = lastMessage.text.length * 30 + 500;
				const timeoutId = setTimeout(() => {
					setStreamingMessageId(null);
				}, streamingDuration);

				return () => {
					clearTimeout(timeoutId);
				};
			}
		}
	}, [messages]);

	return (
		<Paper
			elevation={3}
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "80vh",
				overflow: "hidden",
				borderRadius: 2,
			}}
		>
			<AppBar position="static" sx={{ borderRadius: "8px 8px 0 0", backgroundColor: INK.SECONDARY }}>
				<Toolbar>
					<Box sx={{ flexGrow: 1 }}>
						<Typography variant="h6" component="div" sx={{ color: SURFACE }}>
							Chat
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<AutoAwesome sx={{ fontSize: 14, color: SURFACE, opacity: 0.8 }} />
							<Typography
								variant="caption"
								sx={{
									color: SURFACE,
									opacity: 0.8,
									fontSize: "11px",
								}}
							>
								Powered by Gemini
							</Typography>
						</Box>
					</Box>
				</Toolbar>
			</AppBar>
			<MessageContainer>
				{messages.map((message) => (
					<Stack
						key={message.id}
						direction="row"
						spacing={1}
						sx={{
							alignItems: "center",
							justifyContent: message.sender === "user" ? "flex-end" : "flex-start",
							width: "100%",
						}}
					>
						{message.sender !== "user" && (
							<Avatar sx={{ bgcolor: "secondary.main", width: 32, height: 32 }}>
								<SmartToy sx={{ fontSize: 18 }} />
							</Avatar>
						)}

						<Box sx={{ maxWidth: "70%" }}>
							<MessageBubble isUser={message.sender === "user"}>
								<Typography variant="body1">
									{message.sender !== "user" && streamingMessageId === message.id ? <StreamingMessage message={message.text ?? ""} /> : (message.text ?? "")}
								</Typography>
								<Typography
									variant="caption"
									sx={{
										display: "block",
										textAlign: "right",
										mt: 0.5,
										opacity: 0.7,
									}}
								>
									{DateTime.fromISO(message.timestamp).toFormat("h:mm a")}
								</Typography>
							</MessageBubble>
						</Box>

						{message.sender === "user" && (
							<Avatar src={userImage} sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
								<Person sx={{ fontSize: 18 }} />
							</Avatar>
						)}
					</Stack>
				))}

				{typing && (
					<Stack
						direction="row"
						spacing={1}
						sx={{
							alignItems: "center",
							justifyContent: "flex-start",
							width: "100%",
						}}
					>
						<Avatar sx={{ bgcolor: "secondary.main", width: 32, height: 32 }}>
							<SmartToy sx={{ fontSize: 18 }} />
						</Avatar>

						<Box sx={{ maxWidth: "70%" }}>
							<TypingIndicator>
								<TypingDot sx={{ animation: `${blink} 1.4s infinite 0.2s` }} />
								<TypingDot sx={{ animation: `${blink} 1.4s infinite 0.4s` }} />
								<TypingDot sx={{ animation: `${blink} 1.4s infinite 0.6s` }} />
							</TypingIndicator>
						</Box>
					</Stack>
				)}
				<div ref={messageEndRef} />
			</MessageContainer>
			<Box
				component="form"
				onSubmit={onSendMessage}
				sx={{
					p: 2,
					borderTop: 1,
					borderColor: "divider",
					backgroundColor: "background.paper",
					display: "flex",
					alignItems: "center",
				}}
			>
				<TextField
					fullWidth
					variant="outlined"
					placeholder="Type a message..."
					value={inputMessage}
					onChange={(e) => setInputMessage(e.target.value)}
					disabled={!connected}
					size="small"
					sx={{ mr: 1 }}
				/>
				<IconButton type="submit" color="primary" disabled={!connected || inputMessage.trim() === ""} sx={{ ml: 1 }}>
					<Send />
				</IconButton>
			</Box>
		</Paper>
	);
};
